import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { findTreatmentBySlug, specialties } from "@/lib/healic-data";
import { absUrl, BRAND, breadcrumbJsonLd, surgerySeo } from "@/lib/seo";
import { TreatmentPage, buildContent, type TreatmentLoaderData } from "./treatments.$slug";
import consultationImg from "@/assets/vaginal-tightening/consultation.webp.asset.json";
import anatomyImg from "@/assets/vaginal-tightening/anatomy.webp.asset.json";
import laxityCausesImg from "@/assets/vaginal-tightening/causes-infographic.webp.asset.json";
import benefitReflectImg from "@/assets/vaginal-tightening/benefit-reflect.webp.asset.json";
import benefitSquatsImg from "@/assets/vaginal-tightening/benefit-squats.webp.asset.json";
import signsIconsImg from "@/assets/vaginal-tightening/signs-icons.webp.asset.json";
import laserIllustrationImg from "@/assets/vaginal-tightening/laser-illustration.webp.asset.json";

const SignsSection = (
  <section className="bg-cream">
    <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
      <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
        Signs You May Need Vaginal Tightening
      </h2>
      <div className="mt-9 grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            Many women are unsure whether their symptoms require medical attention. Vaginal laxity develops gradually, so the early signs are easy to overlook. Recognising them helps you decide when to consult a cosmetic gynecologist about vaginal tightening treatment.
          </p>
          <p>You may be a candidate for laser vaginal tightening or another vaginal rejuvenation procedure if you notice:</p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>Reduced vaginal firmness or a feeling of looseness</li>
            <li>Vaginal dryness or discomfort during intimacy</li>
            <li>Mild urinary leakage while coughing, sneezing or exercising</li>
            <li>Reduced sensation and lowered confidence</li>
            <li>Pelvic floor weakness after childbirth</li>
            <li>Tissue changes after menopause or hormonal shifts</li>
          </ul>
          <p>
            These symptoms are common and treatable. A detailed evaluation at Healic determines whether laser vaginal tightening suits you best.
          </p>
        </div>
        <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
          <img
            src={signsIconsImg.url}
            alt="Illustrated icons showing signs of vaginal laxity including reduced firmness, dryness, mild urinary leakage, reduced confidence and pelvic floor weakness"
            width={1200}
            height={800}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </figure>
      </div>
    </div>
  </section>
);

const MethodsSection = (
  <section className="bg-cream">
    <div className="mx-auto max-w-7xl px-4 py-12 sm:pr-16 lg:px-8 lg:py-14 lg:pr-24">
      <div className="text-center">
        <h2 className="font-display text-3xl font-bold sm:text-4xl">
          Vaginal Tightening Methods at Healic
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-[15px] leading-relaxed text-muted-foreground">
          Different patients need different approaches. Our cosmetic gynecologists choose the right vaginal tightening method for your degree of laxity, age and recovery goals.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-[32px] bg-white p-8 shadow-sm ring-1 ring-black/5">
          <h3 className="font-display text-lg font-bold">
            Laser Vaginal Tightening (CO₂ / Er:YAG)
          </h3>
          <p className="mt-4 text-[14px] leading-relaxed text-muted-foreground">
            Painless laser sessions inside the vaginal canal that stimulate new collagen formation — walk-in-walk-out, no cuts, no stitches.
          </p>
        </div>
        <div className="rounded-[32px] bg-white p-8 shadow-sm ring-1 ring-black/5">
          <h3 className="font-display text-lg font-bold">
            Surgical Vaginoplasty
          </h3>
          <p className="mt-4 text-[14px] leading-relaxed text-muted-foreground">
            Surgical reconstruction of vaginal muscles for significant laxity — long-lasting improvement when laser is not enough.
          </p>
        </div>
        <div className="rounded-[32px] bg-white p-8 shadow-sm ring-1 ring-black/5">
          <h3 className="font-display text-lg font-bold">
            Vaginal PRP Therapy
          </h3>
          <p className="mt-4 text-[14px] leading-relaxed text-muted-foreground">
            Autologous PRP injections alongside laser sessions to enhance lubrication, sensation and tissue health — natural and regenerative.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const TypesAndLaserSection = (
  <>
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
        <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
          Types of Vaginal Tightening Treatment
        </h2>
        <div className="mt-9 grid items-center gap-8">
          <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            There is no single vaginal tightening procedure that suits every woman. The right choice depends on the degree of vaginal laxity, your symptoms, medical history and how much downtime you can take.
          </p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li><strong className="text-foreground">Laser vaginal tightening</strong> — non-surgical collagen remodelling with minimal downtime.</li>
            <li><strong className="text-foreground">Supportive maintenance care</strong> — pelvic floor training and follow-up sessions.</li>
          </ul>
          <p>
            At Healic, our cosmetic gynecologists explain the laser option honestly, including expected results, sessions required and laser vaginal tightening cost, before you decide.
          </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
        <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
          What is Laser Vaginal Tightening?
        </h2>
        <div className="mt-9 grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
            <p>
              Laser vaginal tightening is a non-surgical vaginal rejuvenation treatment that uses controlled laser energy to gently heat the vaginal tissue. This heat stimulates natural collagen production and tissue remodelling, gradually improving firmness, elasticity and lubrication.
            </p>
            <p>
              The vaginal tightening laser treatment is performed in an outpatient setting, usually takes 20 to 30 minutes, and does not require general anaesthesia. Most women describe the sensation as mild warmth and return to routine activities the same day.
            </p>
            <p>
              Results build gradually over several weeks as new collagen forms. Depending on the degree of laxity, a course of two to three sessions is commonly advised, with maintenance sessions later if needed.
            </p>
          </div>
          <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <img
              src={laserIllustrationImg.url}
              alt="Medical illustration of laser vaginal tightening showing a laser probe delivering controlled energy to the vaginal wall and collagen fibres remodelling"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </figure>
        </div>
      </div>
    </section>
  </>
);

const LaserBenefitsSection = (
  <section className="bg-background">
    <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
      <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
        Benefits of Laser Vaginal Tightening
      </h2>
      <div className="mt-9 grid items-center gap-8">
        <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            Laser vaginal tightening has become one of the most preferred vaginal rejuvenation options because it delivers meaningful improvement without surgery, stitches or long recovery.
          </p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>Improved vaginal tightness and firmness</li>
            <li>Better tissue elasticity</li>
            <li>Increased natural collagen production</li>
            <li>Improved natural lubrication</li>
            <li>Minimal downtime</li>
            <li>Non-surgical, anaesthesia-free solution</li>
            <li>Quick in-office procedure</li>
            <li>Improved comfort and confidence</li>
          </ul>
          <p>
            Every treatment plan at Healic is personalised, and our cosmetic gynecologists set realistic expectations before you begin.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PRICE_ROWS = [
  {
    treatment: "Laser vaginal tightening",
    range: "₹15,000 – ₹20,000",
    sessions: "1–3 sessions",
    note: "Non-surgical, same-day discharge",
  },
];

function PriceSection() {
  return (
  <section id="cost" className="bg-cream">
    <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
      <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
        Vaginal Tightening Cost in India (2026)
      </h2>
      <p className="mx-auto mt-4 max-w-3xl text-center text-[15px] leading-relaxed text-muted-foreground">
        Laser vaginal tightening cost in India at Healic is typically in the range of ₹15,000 to ₹20,000 per session. We offer fixed-price packages with no hidden charges and EMI options at every branch.
      </p>

      <div className="mt-9 overflow-x-auto rounded-3xl border border-border bg-card shadow-soft">
        <table className="w-full min-w-[640px] border-collapse text-left text-sm">
          <caption className="sr-only">
            Indicative cost of vaginal tightening treatments at Healic in India
          </caption>
          <thead>
            <tr className="bg-muted/50">
              <th scope="col" className="px-5 py-3.5 font-semibold">Treatment</th>
              <th scope="col" className="px-5 py-3.5 font-semibold">Indicative cost</th>
              <th scope="col" className="px-5 py-3.5 font-semibold">Sessions</th>
              <th scope="col" className="px-5 py-3.5 font-semibold">What it includes</th>
            </tr>
          </thead>
          <tbody>
            {PRICE_ROWS.map((r) => (
              <tr key={r.treatment} className="border-t border-border">
                <th scope="row" className="px-5 py-3.5 font-medium text-foreground">{r.treatment}</th>
                <td className="px-5 py-3.5 text-muted-foreground">{r.range}</td>
                <td className="px-5 py-3.5 text-muted-foreground">{r.sessions}</td>
                <td className="px-5 py-3.5 text-muted-foreground">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Indicative ranges only. Your final package is confirmed after an in-person evaluation.
      </p>

      <h3 className="mt-10 text-center font-display text-2xl font-bold">
        City-wise laser vaginal tightening pricing
      </h3>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {CITY_COSTS.map((c) => (
          <a
            key={c.city}
            href={c.href}
            className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft transition hover:border-accent"
          >
            <span>
              <span className="block font-semibold text-foreground">Healic {c.city}</span>
              <span className="block text-xs text-muted-foreground">{c.label}</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-accent" />
          </a>
        ))}
      </div>
    </div>
  </section>
  );
}

const COMPARISON = [
  { label: "Approach", laser: "Non-surgical laser energy" },
  { label: "Anaesthesia", laser: "Usually none; topical numbing" },
  { label: "Session time", laser: "20–30 minutes" },
  { label: "Downtime", laser: "Minimal, same-day routine" },
  { label: "Best suited for", laser: "Mild to moderate vaginal laxity" },
  { label: "Results appear", laser: "Gradually over 4–8 weeks" },
];

const VT_FAQS: { q: string; a: string }[] = [
  { q: "What is vaginal tightening treatment?", a: "Vaginal tightening treatment refers to medical and cosmetic procedures that improve the firmness, elasticity, and strength of vaginal tissues. At Healic, laser vaginal tightening is the preferred approach for suitable candidates. Every treatment plan is personalized after consultation with an experienced cosmetic gynecologist." },
  { q: "What is laser vaginal tightening?", a: "Laser vaginal tightening is a minimally invasive vaginal tightening procedure that uses controlled laser energy to stimulate collagen production within the vaginal tissues. This process helps improve elasticity, tissue support, and overall vaginal health without major surgery." },
  { q: "Is laser vaginal tightening safe?", a: "When performed by qualified cosmetic gynecologists using appropriate technology such as CO₂ Laser or Er:YAG Laser, laser vaginal tightening is generally considered safe for suitable candidates. Your doctor will first evaluate your medical history and determine whether the procedure is appropriate for you." },
  { q: "How much does laser vaginal tightening cost?", a: "The laser vaginal tightening cost depends on factors such as the patient's condition, the type of treatment recommended, and the number of sessions required. At Healic, the laser vaginal tightening procedure typically ranges from ₹15,000 to ₹20,000, with EMI options available. Consultation fees vary by location. The final treatment cost is confirmed after a detailed medical consultation." },
  { q: "What factors affect the laser vaginal tightening cost?", a: "Several factors influence the laser vaginal tightening cost, including: severity of vaginal laxity, number of sessions required, technology used, doctor's experience, and individual treatment plan." },
  { q: "How long does the laser vaginal tightening procedure take?", a: "A typical laser vaginal tightening procedure usually takes 30–45 minutes, depending on the patient's condition and treatment plan." },
  { q: "Is laser vaginal tightening painful?", a: "Most women report only mild discomfort during laser vaginal tightening. The procedure is designed to be minimally invasive, and your doctor will explain the available comfort measures before treatment." },
  { q: "How many sessions of laser vaginal tightening are required?", a: "The number of sessions depends on the severity of vaginal laxity and the treatment goals. Some women may achieve satisfactory results after one session, while others may benefit from additional sessions based on their doctor's recommendation." },
  { q: "What is vaginal rejuvenation?", a: "Vaginal rejuvenation is a broad term used for treatments that improve vaginal tissue health, elasticity, hydration, and overall function. At Healic, laser vaginal rejuvenation is the preferred non-surgical approach for suitable candidates." },
  { q: "What is the difference between vaginal tightening and vaginal rejuvenation?", a: "Vaginal tightening focuses primarily on improving tissue firmness and reducing vaginal laxity. Vaginal rejuvenation is a broader concept that may also address vaginal dryness, tissue quality, lubrication, and overall intimate wellness." },
];

const SPECIALISTS: {
  name: string;
  city: string;
  img: string;
  alt: string;
  credentials: string[];
  profileHref: string;
}[] = [
  {
    name: "Dr. Pragya Richa",
    city: "Gurgaon",
    img: "https://cdn.prod.website-files.com/68a9318302af9aa98fc92c3a/693babc7d1447cfc7059abe8_68dff39790fb664bb59ae241_Dr.%2520Pragya%2520Richa.png",
    alt: "Dr. Pragya Richa, cosmetic gynecologist at Healic Gurgaon specializing in laser vaginal tightening and vaginal rejuvenation",
    credentials: [
      "MBBS, MS (Obstetrics & Gynaecology)",
      "10+ Years of Experience",
      "Specialist in Cosmetic Gynecology, Laser Vaginal Tightening, Vaginal Rejuvenation, and Women's Wellness",
    ],
    profileHref: "/doctors/pragya-richa",
  },
  {
    name: "Dr. Kanika Bhadhkaria",
    city: "Agra",
    img: "/__l5e/assets-v1/ea975dc0-edbe-4e9c-b0b6-f9e707bd738c/dr-kanika-bhadhkaria.jpeg",
    alt: "Dr. Kanika Bhadhkaria, consultant obstetrician and gynecologist at Healic Agra specializing in laser vaginal tightening and vaginal rejuvenation",
    credentials: [
      "Consultant Obstetrician & Gynecologist",
      "10+ Years of Experience",
      "Expertise in Cosmetic Gynecology, Laser Vaginal Tightening, and Vaginal Rejuvenation",
    ],
    profileHref: "/doctors/dr-kanika-bhadhkaria-obstetrics-gynaecology-agra",
  },
  {
    name: "Dr. Deepika Yadav",
    city: "Haridwar",
    img: "https://cdn.prod.website-files.com/68a9318302af9aa98fc92c3a/693babb903479dedb3fd34d6_6937e6a5b619fec95dd31f9a_Dr.%2520Deepika%2520Yadav.png",
    alt: "Dr. Deepika Yadav, gynecologist at Healic Haridwar specializing in laser vaginal tightening and women's wellness",
    credentials: [
      "MBBS",
      "9+ Years of Experience",
      "Expertise in Cosmetic Gynecology, Pregnancy Care, Laser Vaginal Tightening, and Women's Wellness",
    ],
    profileHref: "/doctors/deepika-yadav",
  },
];

const CITY_COSTS = [
  { city: "Agra", label: "View Agra Pricing", href: "https://healic.in/agra/laser-vaginal-tightening-cost" },
  { city: "Haridwar", label: "View Haridwar Pricing", href: "https://healic.in/haridwar/laser-vaginal-tightening-cost" },
  { city: "Gurgaon", label: "View Gurgaon Pricing", href: "https://healic.in/gurgaon/laser-vaginal-tightening-cost" },
];

const HEALIC_ORG_ID = "https://healic.in/#organization";

const BRANCH_ADDRESS: Record<string, Record<string, string>> = {
  Gurgaon: {
    "@type": "PostalAddress",
    streetAddress: "68 Avenue, Sec-68, Badshahpur, Sohna Road",
    addressLocality: "Gurugram",
    addressRegion: "Haryana",
    postalCode: "122101",
    addressCountry: "IN",
  },
  Agra: {
    "@type": "PostalAddress",
    streetAddress: "Plot No-1 A, Bodla Rd, Shahganj",
    addressLocality: "Agra",
    addressRegion: "Uttar Pradesh",
    postalCode: "282012",
    addressCountry: "IN",
  },
  Haridwar: {
    "@type": "PostalAddress",
    streetAddress: "Ranipur More, Haridwar Main Rd",
    addressLocality: "Haridwar",
    addressRegion: "Uttarakhand",
    postalCode: "249401",
    addressCountry: "IN",
  },
};

// Combined @graph JSON-LD for the /cosmetic-gynecology/vaginal-tightening page.
function vaginalTighteningGraph(title: string, description: string, pageUrl: string) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalWebPage",
        "@id": `${pageUrl}#webpage`,
        name: title,
        description,
        url: pageUrl,
        inLanguage: "en-IN",
        lastReviewed: "2026-08-13",
        medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
        publisher: { "@id": HEALIC_ORG_ID },
        about: {
          "@type": "MedicalProcedure",
          name: "Vaginal Tightening",
          alternateName: ["Laser Vaginal Tightening", "Vaginal Rejuvenation"],
          procedureType: "https://schema.org/NoninvasiveProcedure",
          relevantSpecialty: { "@type": "MedicalSpecialty", name: "Cosmetic Gynecology" },
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: VT_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      ...SPECIALISTS.map((d) => ({
        "@type": "Physician",
        "@id": `https://healic.in${d.profileHref}#physician`,
        name: d.name,
        url: `https://healic.in${d.profileHref}`,
        image: d.img.startsWith("http") ? d.img : `https://healic.in${d.img}`,
        medicalSpecialty: ["http://schema.org/Obstetric", "http://schema.org/Gynecologic"],
        telephone: "+91-95887-95887",
        address: BRANCH_ADDRESS[d.city] ?? BRANCH_ADDRESS.Gurgaon,
        parentOrganization: { "@id": HEALIC_ORG_ID },
      })),

    ],
  };
}



const BenefitSection = (
  <section className="bg-cream">
    <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
      <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
        Who Can Benefit from Vaginal Tightening Treatment?
      </h2>
      <div className="mt-9 grid items-start gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            Many women wonder whether they are suitable candidates for vaginal tightening treatment. While every patient requires an individual medical assessment, several situations may indicate that consultation with a cosmetic gynecologist is beneficial.
          </p>
          <p>
            You may benefit from laser vaginal tightening or another vaginal rejuvenation procedure if you experience:
          </p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>A feeling of vaginal looseness</li>
            <li>Reduced vaginal firmness after childbirth</li>
            <li>Mild stress urinary incontinence</li>
            <li>Vaginal dryness associated with menopause</li>
            <li>Reduced confidence during intimacy</li>
            <li>Decreased vaginal elasticity</li>
            <li>Age-related vaginal changes</li>
            <li>Mild pelvic floor weakness</li>
            <li>Desire for non-surgical vaginal rejuvenation</li>
            <li>Interest in improving vaginal wellness</li>
          </ul>
          <p>
            Women seeking best vaginal rejuvenation options often prefer laser vaginal tightening because it is minimally invasive, requires little downtime, and stimulates the body's natural collagen production.
          </p>
          <p>
            During your consultation at Healic, our cosmetic gynecologists will assess your medical history, symptoms, and expectations to recommend whether laser vaginal tightening is the right approach for you.
          </p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
          <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <img
              src={benefitReflectImg.url}
              alt="Woman relaxing at home, reflecting on her vaginal health and wellness options"
              width={1000}
              height={667}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </figure>
          <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <img
              src={benefitSquatsImg.url}
              alt="Woman doing squats at home as part of pelvic floor strengthening and fitness routine"
              width={1000}
              height={667}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </figure>
        </div>
      </div>
    </div>
  </section>
);

const LaxityCausesSection = (
  <section className="bg-background">
    <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
      <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
        Common Causes of Vaginal Laxity
      </h2>
      <div className="mt-9 grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            Vaginal laxity is a common concern among women of different age groups and is one of the primary reasons they seek vaginal tightening treatment. Contrary to popular belief, vaginal laxity is not limited to women who have given birth. Factors such as aging, hormonal changes, menopause, genetics, and lifestyle can also reduce the elasticity and strength of vaginal tissues.
          </p>
          <p>
            Modern laser vaginal tightening treatment helps restore tissue strength by improving collagen production and supporting natural healing.
          </p>
          <p>
            Understanding the underlying cause is essential for selecting the most effective treatment plan. At Healic, our cosmetic gynecologists perform a comprehensive evaluation before recommending laser vaginal tightening or the right supportive care.
          </p>
        </div>
        <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
          <img
            src={laxityCausesImg.url}
            alt="Circular infographic showing common causes of vaginal laxity: childbirth, aging, menopause, hormonal changes, weight loss, genetics, and weak pelvic floor muscles"
            width={885}
            height={432}
            loading="lazy"
            decoding="async"
            className="h-auto w-full object-contain"
          />
        </figure>
      </div>
    </div>
  </section>
);

const COSMETIC_SPECIALTY = "cosmetic-gynaecology";

const CITY_MAP: Record<string, string> = {
  agra: "Agra",
  haridwar: "Haridwar",
  gurgaon: "Gurgaon",
  gurugram: "Gurugram",
};

function parseCitySuffix(rawSlug: string): { slug: string; city?: string } {
  for (const key of Object.keys(CITY_MAP)) {
    const suffix = `-in-${key}`;
    if (rawSlug.endsWith(suffix)) {
      return { slug: rawSlug.slice(0, -suffix.length), city: CITY_MAP[key] };
    }
  }
  return { slug: rawSlug };
}

type LoaderData = TreatmentLoaderData & { city?: string; rawSlug: string };

export const Route = createFileRoute("/cosmetic-gynecology/$slug")({
  loader: ({ params }): LoaderData => {
    const { slug, city } = parseCitySuffix(params.slug);
    const treatment = findTreatmentBySlug(slug);
    if (!treatment || treatment.specialtySlug !== COSMETIC_SPECIALTY) throw notFound();
    const specialty = specialties.find((s) => s.slug === treatment.specialtySlug)!;
    const related = specialty.treatments
      .filter((t: string) => t !== treatment.name)
      .filter((t: string) => !(treatment.slug === "vaginal-tightening" && ["Vaginoplasty", "Vaginal PRP"].includes(t)))
      .slice(0, 4);
    const content = buildContent(treatment.name, specialty.name, treatment.slug);
    return { treatment, specialty, related, content, city, rawSlug: params.slug };
  },
  head: ({ loaderData }) => {
    const t = loaderData?.treatment;
    const c = loaderData?.content;
    if (!t || !c) return { meta: [{ title: "Cosmetic Gynaecology — Healic" }] };
    const city = loaderData?.city;
    const path = `/cosmetic-gynecology/${loaderData!.rawSlug}`;
    const url = absUrl(path);
    const seo = surgerySeo({
      name: t.name,
      slug: t.slug,
      short: c.short,
      overview: c.overview,
      specialtyName: loaderData?.specialty.name,
    });
    const titleBase = city ? `${t.name} in ${city}` : seo.shortName;
    const isVaginalTightening = loaderData?.rawSlug === "vaginal-tightening";
    const title = isVaginalTightening
      ? "Vaginal Tightening Treatment | Laser Vaginal Tightening Cost | Healic"
      : city
        ? `${t.name} in ${city} — Cost, Best Doctors & Recovery | Healic`
        : seo.title;
    const description = isVaginalTightening
      ? "Discover everything about vaginal tightening treatment, including laser vaginal tightening, vaginal rejuvenation, benefits, procedure, recovery, and laser vaginal tightening cost. Consult experienced cosmetic gynecologists at Healic for safe and personalized treatment."
      : city
        ? `Looking for ${t.name.toLowerCase()} in ${city}? ${c.short} Expert female gynaecologists, advanced lasers and complete privacy at Healic ${city}.`
        : seo.description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        ...(city ? [{ name: "geo.placename", content: city }] : []),
        { property: "og:title", content: `${titleBase} — ${BRAND}` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: BRAND },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${titleBase} — Healic` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ...(isVaginalTightening
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(vaginalTighteningGraph(title, description, url)),
              },
            ]
          : [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "MedicalProcedure",
                  name: city ? `${t.name} in ${city}` : t.name,
                  alternateName: seo.shortName,
                  description: c.overview,
                  howPerformed: c.procedure.join(". "),
                  followup: c.recovery,
                  url,
                  relevantSpecialty: { "@type": "MedicalSpecialty", name: loaderData?.specialty.name },
                  ...(city ? { location: { "@type": "Hospital", name: `Healic ${city}` } } : {}),
                }),
              },
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: c.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]),
        breadcrumbJsonLd([
          { name: "Home", url: absUrl("/") },
          { name: "Cosmetic Gynaecology", url: absUrl(`/specialties/${COSMETIC_SPECIALTY}`) },
          { name: city ? `${seo.shortName} in ${city}` : seo.shortName, url },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Treatment not found</h1>
        <Link
          to="/specialties/$slug"
          params={{ slug: COSMETIC_SPECIALTY }}
          className="mt-6 inline-block text-accent"
        >
          Browse cosmetic gynaecology →
        </Link>
      </div>
    </PageShell>
  ),
  component: () => {
    const data = Route.useLoaderData() as LoaderData;
    const overrides =
      data.rawSlug === "vaginal-tightening"
        ? {
            heroTitle: "Vaginal Tightening Treatment",
            heroBody: (
              <>
                <p>
                  Many women experience changes in vaginal firmness due to childbirth, aging, menopause, hormonal changes, or significant weight fluctuations. These natural changes may affect confidence, comfort during daily activities, intimate wellness, and overall quality of life. Fortunately, modern vaginal tightening treatment offers safe and effective solutions that help restore vaginal tone without compromising comfort or privacy.
                </p>
                <p>
                  At Healic, we provide advanced laser vaginal tightening treatment using the latest laser technology. Our experienced cosmetic gynecologists create personalized treatment plans based on each patient's medical condition, symptoms, expectations, and lifestyle.
                </p>
                <p>
                  Whether you are looking for laser vaginal tightening, want to understand the vaginal tightening procedure, or learn about laser vaginal tightening cost, this comprehensive guide covers everything you need to make an informed decision.
                </p>
                <p>
                  Our specialists focus on improving vaginal health, restoring tissue elasticity, enhancing intimate wellness, and helping women regain confidence through evidence-based cosmetic gynecology treatments.
                </p>
              </>
            ),
            heroMedia: (
              <figure className="mt-6 max-w-2xl overflow-hidden rounded-3xl ring-1 ring-white/20">
                <img
                  src={consultationImg.url}
                  alt="Cosmetic gynecologist consulting with patient at Healic"
                  width={1200}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </figure>
            ),
            aboutHeading: "What is Vaginal Tightening?",
            heroImageSrc: anatomyImg.url,
            heroImageAlt:
              "Cross-sectional medical illustration of the female reproductive system showing the uterus, fallopian tubes, ovaries, and vaginal canal",
            aboutBody: (
              <>
                <p>
                  Vaginal tightening refers to medical and cosmetic procedures designed to improve the firmness, elasticity, and strength of vaginal tissues. The goal of vaginal tightening treatment is to address vaginal laxity caused by childbirth, aging, menopause, or reduced collagen production while improving both functional and aesthetic concerns.
                </p>
                <p>
                  Modern vaginal tightening treatment can be performed using minimally invasive laser vaginal tightening or laser vaginal rejuvenation. The choice of treatment depends on the degree of vaginal laxity, the patient's symptoms, medical history, and desired results.
                </p>
                <p>
                  Among all available options, laser vaginal tightening has become one of the most preferred treatments because it is non-surgical, involves minimal discomfort, requires little downtime, and stimulates the body's natural collagen production. During the vaginal tightening laser treatment, controlled laser energy gently heats the vaginal tissue, encouraging collagen remodeling and tissue regeneration. Over time, this helps improve elasticity, lubrication, and overall vaginal support.
                </p>
                <p>
                  At Healic, every vaginal tightening procedure begins with a detailed consultation by an experienced cosmetic gynecologist. The doctor evaluates your symptoms, discusses your expectations, and recommends the most appropriate laser vaginal tightening treatment plan.
                </p>
              </>
            ),
            symptomsHeading: "Benefits of Vaginal Tightening",
            causesHeading: "Causes of Vaginal Laxity",
            methodsHeading: "Vaginal Tightening Methods at Healic",
            methodsIntro:
              "Different patients need different approaches. Our cosmetic gynecologists choose the right vaginal tightening method for your degree of laxity, age and recovery goals.",
            methodsSection: MethodsSection,
            afterStoriesSection: (
              <>
                {BenefitSection}
                {SignsSection}
                {TypesAndLaserSection}
                {LaserBenefitsSection}
                <PriceSection />
              </>
            ),
            faqs: VT_FAQS,
            faqHeading: "Frequently Asked Questions About Vaginal Tightening Treatment",
            afterFaqSection: (
              <div className="mt-6 border-t border-border pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Medical Disclaimer</p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  The information provided on this page is intended for educational purposes only and should not be considered a substitute for professional medical advice, diagnosis, or treatment. Every woman's condition is unique, and the most appropriate vaginal tightening treatment or laser vaginal tightening procedure should be determined after consultation with a qualified cosmetic gynecologist. Results vary based on individual medical conditions, age, lifestyle, and adherence to post-treatment care. If you experience symptoms related to vaginal health or have questions about laser vaginal tightening, consult an experienced specialist at Healic for a comprehensive evaluation.
                </p>
              </div>
            ),
          }
        : undefined;
    return <TreatmentPage data={data} overrides={overrides} />;
  },
});
